import { useMutation } from '@tanstack/react-query';
import { type FormEvent, type ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@autop/ui/src/button';
import { Field } from '@autop/ui/src/field';

import { SocialLoginButtons } from '@/features/social-login';
import { ConsentAgreement, type ConsentType, hasAllRequiredConsents } from '@/entities/consent';
import { errorCodeOf, useTRPC } from '@/shared/api';
import { loginPathWithNext } from '@/shared/lib/auth-paths';
import { toInternalPath } from '@/shared/lib/internal-path';
import { PasswordField } from '@/shared/ui/password-field';

import { nameFromEmail } from '../model/signup-name';

/** 서버가 준 문구가 없을 때 보여줄 말. 영어 원문(`Failed to fetch`)을 그대로 띄우지 않는다. */
const FALLBACK_MESSAGE = '가입하지 못했습니다. 잠시 후 다시 시도해 주세요.';

/** 이미 가입된 이메일에 서버가 주는 코드. 이때만 로그인으로 가는 길을 함께 낸다. */
const CONFLICT = 'CONFLICT';

/**
 * 서버 스키마와 같은 값 (`apps/api/src/modules/auth/dto/auth.schema.ts`).
 *
 * 브라우저가 먼저 막게 해 둔다 — 스키마에 걸릴 값을 보내 봐야 왕복만 낭비하고,
 * 가입 시도 제한(IP 시간당 10회)까지 그만큼 축낸다. 검증은 서버가 여전히 다시 한다.
 */
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 128;
const MAX_EMAIL_LENGTH = 255;

type Failure = { message: string; isAlreadyRegistered: boolean };

const toMessage = (error: unknown): string =>
  error instanceof Error && error.message.length > 0 ? error.message : FALLBACK_MESSAGE;

/** 비밀번호 강도 — 길이·문자종류로만 판단한다. 서버 검증을 대신하지 않는다. */
function passwordStrength(value: string): { level: 0 | 1 | 2 | 3; label: string } {
  if (value.length === 0) return { level: 0, label: '' };
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z0-9]/].filter((re) => re.test(value)).length;
  if (value.length < 8) return { level: 1, label: '약함 — 8자 이상이어야 합니다' };
  if (kinds >= 3) return { level: 3, label: '강함' };
  return { level: 2, label: '보통 — 숫자·기호를 섞으면 강해집니다' };
}

/**
 * 회원가입 — 1차 오픈 범위 §6.1 (/signup). 이 화면이 곧 무료자료 게이트다.
 *
 * 이메일 폼을 먼저 두고 소셜을 그 **아래**에 둔다. 게이트에서 받아야 하는 것은
 * 「가입」이 아니라 **이메일과 동의**이므로, 그 둘이 먼저 보여야 화면의 목적이 드러난다.
 * 소셜은 대안 경로로 남긴다.
 *
 * **닉네임은 여기서 받지 않는다.** 게이트에 필드를 하나 더 놓으면 그만큼 덜 넘어오는데,
 * 온보딩에는 「건너뛰기」가 없어서 어차피 다음 화면에서 반드시 받게 된다(§6.2).
 * 서버가 요구하는 이름은 이메일 아이디로 채운다 (`model/signup-name.ts`).
 *
 * **가입해도 로그인되지 않는다.** 서버는 인증 메일을 보내고 세션을 주지 않으며,
 * 인증 전 로그인은 403 으로 막힌다. 그래서 이 화면의 끝은 온보딩이 아니라 「메일함 확인」이다 —
 * 세션이 없으면 온보딩 화면에 설 수 없다.
 */
export function SignupPage(): ReactElement {
  const trpc = useTRPC();
  const [params] = useSearchParams();
  // 주소창의 값이라 그대로 믿지 않는다 — 소셜 로그인과 로그인 화면에 그대로 넘어가는 목적지다.
  const next = toInternalPath(params.get('next'), '/library');

  const { mutateAsync, isPending } = useMutation(trpc.auth.signup.mutationOptions());

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [granted, setGranted] = useState<ReadonlySet<ConsentType>>(new Set());
  const [isMailSent, setIsMailSent] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const strength = passwordStrength(password);
  const requiredMet = hasAllRequiredConsents(granted);

  /**
   * 연타 방어. `isPending` 은 뮤테이션 구독을 거쳐 갱신되므로 클릭과 버튼이 잠기는 사이에
   * 틈이 있다. 가입은 그 틈으로 두 번 나가면 시도 제한(IP 시간당 10회)을 그만큼 축낸다.
   */
  const isSubmitting = useRef(false);

  /**
   * 결과를 `useMutation` 의 상태가 아니라 **`mutateAsync` 의 약속**으로 받는다 —
   * 뮤테이션 상태는 관찰자 구독에 실려 오는데, 그 구독이 끊겼다 붙는 틈에 끝난 결과를
   * 못 받는 자리가 있다(`/verify` 가 성공하고도 대기 화면에 멈춘 적이 있다).
   */
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!requiredMet || isSubmitting.current) return;
    isSubmitting.current = true;
    setFailure(null);

    try {
      // 필수 3종은 서버 스키마가 `true` 리터럴로 받는다 — 위 검사를 지나야만 이 객체를
      //   만들 수 있으므로, 검사를 빠뜨린 경로가 생기지 않는다.
      await mutateAsync({
        email,
        password,
        name: nameFromEmail(email),
        consents: {
          AGE_OVER_14: true,
          TERMS_OF_SERVICE: true,
          PRIVACY_POLICY: true,
          MARKETING_EMAIL: granted.has('MARKETING_EMAIL'),
        },
      });
      setIsMailSent(true);
    } catch (error) {
      setFailure({
        message: toMessage(error),
        isAlreadyRegistered: errorCodeOf(error) === CONFLICT,
      });
    }
  };

  if (isMailSent) {
    return <MailSentNotice next={next} />;
  }

  return (
    <div className="u-container flex justify-center py-16">
      <div className="flex w-full max-w-[26rem] flex-col gap-6">
        <h1 className="text-center text-[1.625rem] font-bold tracking-[-0.04em]">회원가입</h1>

        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
          <Field
            id="signup-email"
            type="email"
            label="이메일"
            placeholder="name@example.com"
            required
            maxLength={MAX_EMAIL_LENGTH}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <PasswordField
            id="signup-password"
            label="비밀번호"
            placeholder="••••••••"
            required
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            hint={strength.label || '8자 이상, 문자·숫자·기호를 섞어 주세요'}
          />
          {strength.level > 0 ? (
            <div className="flex gap-1" aria-hidden="true">
              {[1, 2, 3].map((step) => (
                <span
                  key={step}
                  className={`h-1 flex-1 rounded-full ${
                    step <= strength.level ? 'bg-foreground' : 'bg-border'
                  }`}
                />
              ))}
            </div>
          ) : null}
          <ConsentAgreement granted={granted} onChange={setGranted} idPrefix="signup" />

          <Button type="submit" size="lg" className="w-full" disabled={!requiredMet || isPending}>
            가입하고 시작하기
          </Button>
          <p className="text-center text-[12.5px] text-muted-foreground">
            가입하시면 인증 메일이 발송됩니다. 메일의 링크를 눌러야 로그인할 수 있습니다.
          </p>
        </form>

        {failure === null ? null : <FailureNotice failure={failure} next={next} />}

        <div className="flex items-center gap-3 text-[12.5px] text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          또는 간편하게
          <span className="h-px flex-1 bg-border" />
        </div>

        <SocialLoginButtons intent="signup" next={next} />

        <p className="text-center text-[13px] text-muted-foreground">
          이미 계정이 있으신가요?{' '}
          <Link to="/login" className="font-medium text-foreground underline underline-offset-4">
            로그인
          </Link>
        </p>
      </div>
    </div>
  );
}

/**
 * 가입 완료 — **세션은 없다.**
 *
 * 서버는 인증 메일을 보내고 세션을 주지 않는다. 그래서 여기서 할 수 있는 안내는
 * "메일함을 여세요" 하나뿐이고, 온보딩·자료실로 보내면 그 화면들이 로그인 벽에 막힌다.
 */
function MailSentNotice({ next }: { next: string }): ReactElement {
  /**
   * 제목으로 포커스를 옮긴다.
   *
   * 이 화면은 폼을 통째로 갈아 끼운다 — 방금 누른 버튼이 사라지면서 포커스가 문서 처음으로
   * 튕기는데, 화면을 못 보는 사용자에게는 아무 일도 일어나지 않은 것과 같다.
   */
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    heading.current?.focus();
  }, []);

  return (
    <div className="u-container flex justify-center py-24">
      <div className="flex w-full max-w-[30rem] flex-col items-center gap-5 text-center">
        <h1
          ref={heading}
          tabIndex={-1}
          className="text-[1.375rem] font-bold tracking-[-0.04em] outline-none"
        >
          메일함을 확인해 주세요
        </h1>
        <p className="text-[14px] leading-[1.8] text-muted-foreground">
          가입을 마치려면 이메일 인증이 필요합니다. 보내 드린 메일의 링크를 눌러 주세요.
          <br />
          메일이 오지 않았다면 스팸함도 확인해 주세요.
        </p>
        <Link
          to={loginPathWithNext(next)}
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/85"
        >
          로그인하러 가기
        </Link>
      </div>
    </div>
  );
}

/**
 * 가입 실패.
 *
 * 이미 가입된 이메일이면 **사실대로 알린다.** 모르면 사용자가 로그인으로 갈 방법이 없다 —
 * 계정 열거를 막는 몫은 서버의 요청 제한이 진다(로그인·재설정은 반대로 모호하게 답한다).
 */
function FailureNotice({ failure, next }: { failure: Failure; next: string }): ReactElement {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-destructive border-l-[3px] px-4 py-3">
      <p role="alert" className="text-[13px] text-destructive">
        {failure.message}
      </p>
      {failure.isAlreadyRegistered ? (
        <Link
          to={loginPathWithNext(next)}
          className="text-[13px] font-medium underline underline-offset-4"
        >
          로그인하러 가기
        </Link>
      ) : null}
    </div>
  );
}
